import {
    DestroyRef,
    EnvironmentInjector,
    inject, Injectable,
    runInInjectionContext
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
    ActivatedRoute, ActivatedRouteSnapshot, Navigation, NavigationEnd, NavigationError, NavigationExtras,
    RedirectCommand, ResolveFn, Router, RouterStateSnapshot, UrlTree
} from "@angular/router";
import {
    BehaviorSubject, distinctUntilChanged, filter, map, Observable, of, pairwise, switchMap
} from "rxjs";
import { IRouteData } from "../interfaces/route-data";
import { IActiveRoute } from "../interfaces/active-route";
import { NavigationSource } from "../enums/navigation-source";
import {
    APP_ERROR_NAV_COMMAND_DATA, APP_ROOT_NAV_COMMAND_DATA, APP_LOGOUT_NAV_COMMAND_DATA
} from "../constants/app-tokens";
import { INavigationOptions } from "../interfaces/navigation-options";
import { LoggingService } from "../../services/logging/logging.service";
import {
    APP_FEATURE_GROUP_ID, NG_ROUTE_PATH_DIVIDER
} from "../constants/app-constants";
import { InvalidParametersException } from "../../exceptions/invalid-parameters-exception";
import {
    FeatureGroupNavCommandData, RouteNavCommandData
} from "../models/route-nav-command";
import { AppUtil } from "../utils/app-util";
import { ISidenavLink } from "../../iapi-application/interfaces/sidenav-link";
import { RxjsUtil } from "../../utils/rxjs-util";

/**
 * This service is used to wrap the Angular Router and provide additional functionality
 * for determining the current route and active routes
 *
 * This service is used in the CommonErrorHandlerService so it needs to do its own error handling
 */
@Injectable()
export class MgdRouterService {

    /**
     * The path for the Application Root route
     * i.e. "/"
     */
    readonly rootPath: string;

    /**
     * The default path for the Error route
     * used in errorPath getter
     * i.e. "/error"
     */
    private readonly _defaultErrorPath: string;

    /**
     * The optional path for the logout route
     * used in logoutPath getter
     * i.e. "/logout"
     */
    private readonly _logoutPath?: string;

    /**
     * Maintains the IActiveRoute state which is updated when the rout changes
     */
    private readonly _routeChange$: BehaviorSubject<IActiveRoute>;

    /**
     * The angular router
     */
    private readonly _router: Router = inject(Router);

    /**
     * The reference for when this service is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * The logging service for the application
     */
    private readonly _loggingService = inject(LoggingService);

    /**
     * The environment injector used to run resolvers in the correct injection context
     */
    private readonly _injector = inject(EnvironmentInjector);

    /**
     * Emits an IRouteChange whenever the route changes
     * If the route is legacy it will emit undefined, and will not emit again until the route
     * changes to a non-legacy route
     * otherwise it will only emit when the routeId and/or featureGroupId changes
     */
    get routeChange$(): Observable<IActiveRoute> {

        return this._routeChange$.pipe(
            // Only emit when the route changes
            // If either the current or previous route is legacy we consider this changed
            distinctUntilChanged((previous, current) =>
                previous?.featureGroupId === current?.featureGroupId
                && previous?.routeId === current?.routeId)
        );
    }

    /**
     * Get the Url for the root of the application
     */
    get rootUrl(): UrlTree {

        return this._router.parseUrl(this.rootPath);
    }

    /**
     * Get the path to use on error
     */
    get errorPath(): string {

        return this.activeRoute?.errorRedirectPath ?? this._defaultErrorPath;
    }

    /**
     * Get the Url to use on error
     */
    get errorUrl(): UrlTree {

        return this._router.parseUrl(this.errorPath);
    }

    /**
     * Get the path for the logout route
     *
     * If the application doesn't have authentication enabled this will return the errorPath after logging an error
     */
    get logoutPath(): string {

        if (!this._logoutPath) {
            this._loggingService.logError("logoutPath accessed in application that doesn't have authentication");
            return this.errorPath;
        }
        return this._logoutPath;
    }

    /**
     * Get the path for the logout route
     *
     * If the application doesn't have authentication enabled this will return the errorUrl after logging an error
     */
    get logoutUrl(): UrlTree {

        return this._router.parseUrl(this.logoutPath);
    }

    /**
     * Returns the metadata for the active route
     *
     * If the route is on a legacy route it will return undefined
     */
    get activeRoute(): IActiveRoute {

        return this._routeChange$.value;
    }

    /**
     * Returns the current ActivatedRoute from the angular router
     */
    get route(): ActivatedRoute {

        let route: ActivatedRoute = this._router.routerState.root;
        while (route?.firstChild) {
            route = route.firstChild;
        }
        return route;
    }

    /**
     * Returns the error message from the navigation state if any
     */
    get navigationErrorMessage(): string | null {

        return this._navigation?.extras?.state?.errorMessage;
    }

    /**
     * Returns the NavigationSource for navigation
     * Will use the current navigation if navigation is in progress otherwise it will use the last successful navigation
     *
     * If source is unknown it will return undefined.
     */
    get navigationSource(): NavigationSource {

        return this._navigation?.extras?.info as NavigationSource;
    }

    /**
     * Returns the current Navigation from the Angular Router
     *
     * If not currently Navigating it returns undefined
     */
    get currentNavigation(): Navigation {

        return this._router.getCurrentNavigation();
    }

    /**
     * True until the first navigation completes, i.e. while the app is handling a deep link or reload.
     * A guard returning false then would leave the user on a blank page, so it should redirect instead.
     *
     * @returns True while no navigation has completed yet
     */
    get isInitialNavigation(): boolean {

        return !this._router.navigated;
    }

    /**
     * Returns the current or last successful navigation
     */
    private get _navigation(): Navigation {

        return this._router.getCurrentNavigation() ?? this._router.lastSuccessfulNavigation;
    }

    /**
     * Default constructor
     * Subscribes to the router events to set routeChange$
     *
     * Subscribes to routeChange$ to setup the onExit callback
     */
    constructor() {

        // Initialize the routeChange state with the current ActiveRoute
        this._routeChange$ = new BehaviorSubject<IActiveRoute>(this._createActiveRoute());
        this._setupRouteChangeSubscription();
        this.rootPath = inject(APP_ROOT_NAV_COMMAND_DATA).getPath();
        this._defaultErrorPath = inject(APP_ERROR_NAV_COMMAND_DATA).getPath();
        this._logoutPath = inject(APP_LOGOUT_NAV_COMMAND_DATA, { optional: true })?.getPath();
    }

    /**
     * Navigate using the commands relative to to the Angular Router Route
     *
     * @see INavigationOptions for additional options
     */
    navigate(
        commands: ReadonlyArray<string> | Array<string>,
        source: NavigationSource,
        options?: INavigationOptions
    ): void {

        if (!source) {
            throw new Error("source is required");
        }
        this._router.navigate(commands as Array<string>, this._getNavigationExtras(source, options)).catch((error) => {
            this._loggingService.logError(`Failed to navigate to ${commands.join(NG_ROUTE_PATH_DIVIDER)}`, error);
            return false;
        });
    }

    /**
     * Navigate to the route
     *
     * the relativeToCurrentRoute and queryParams excluded, since they would have been defined when the urlTree was created
     * @see INavigationOptions for additional options
     */
    navigateToRoute(
        route: ActivatedRoute,
        source: NavigationSource,
        options?: INavigationOptions
    ): void {

        if (!route) {
            throw new InvalidParametersException("route is required");
        }
        if (!source) {
            throw new Error("source is required");
        }
        const url = route.pathFromRoot.flatMap((current) => current.snapshot.url.map((segment) => segment.path));
        this._router.navigate(url, this._getNavigationExtras(source, options)).catch((error) => {
            this._loggingService.logError(`Failed to navigate to ${url.toString()}`, error);
            return false;
        });
    }

    /**
     * Navigate using the url relative to to the Angular Router Route
     *
     * the relativeToCurrentRoute and queryParams excluded, since they would have been defined when the urlTree was created
     * @see INavigationOptions for additional options
     */
    navigateByUrl(
        url: string | UrlTree,
        source: NavigationSource,
        options?: INavigationOptions
    ): void {

        if (!source) {
            throw new Error("source is required");
        }
        this._router.navigateByUrl(url, this._getNavigationExtras(source, options)).catch((error) => {
            this._loggingService.logError(`Failed to navigate to ${url.toString()}`, error);
            return false;
        });
    }

    /**
     * Returns a UrlTree relative to to the Angular Router Root
     *
     * @see INavigationOptions for additional options
     * Only relative toCurrentRoute and queryParams are allowed
     */
    getUrlTree(
        command: ReadonlyArray<string> | Array<string>,
        options?: Pick<INavigationOptions, "relativeToCurrentRoute" | "queryParams">
    ): UrlTree {

        return this._router.createUrlTree(command as Array<string>, this._getNavigationExtras(undefined, options));
    }

    /**
     * Navigate using the commands relative to to the Angular Router Route
     *
     * The relativeToCurrentRoute and queryParams excluded, since they would have been defined when teh urlTree was created
     * @see INavigationOptions for additional options
     */
    getRedirectCommand(
        url: UrlTree,
        source: NavigationSource,
        // relativeToCurrentRoute and query params would have been defined when the urlTree was created
        options?: Exclude<INavigationOptions, "relativeToCurrentRoute" | "queryParams">
    ): RedirectCommand {

        return new RedirectCommand(url, this._getNavigationExtras(source, options));
    }

    /**
     * Returns a redirect command to the the error route
     *
     * The errorMessage will appear on the error page
     */
    getErrorRedirectCommand(errorMessage: string): RedirectCommand {

        return this.getRedirectCommand(this.errorUrl, NavigationSource.Error, { errorMessage: errorMessage });
    }

    /**
     * Return a redirect command to the logout route
     * If an error message is provided it will be displayed to the user
     */
    getLogoutRedirectCommand(source: NavigationSource, errorMessage?: string): RedirectCommand {

        return this.getRedirectCommand(this.logoutUrl, source, { errorMessage: errorMessage });
    }

    /**
     * Return a redirect command to the Application Root
     */
    getRootRedirectCommand(source: NavigationSource): RedirectCommand {

        return this.getRedirectCommand(this.rootUrl, source);
    }

    /**
     * Will emit whenever the service becomes inactive
     */
    onFeatureGroupExit$(featureGroupId: string): Observable<void> {

        return this.routeChange$.pipe(
            // Grab the previous and current IActiveRoute for comparison
            pairwise(),
            // If the previous route had the service id and the current route does not
            filter(([previous, current]) => previous?.activeFeatureGroupIds?.has(featureGroupId)
                && !current.activeFeatureGroupIds.has(featureGroupId)),
            // Void it
            map(() => void 0)
        );
    }

    /**
     * Emits true if whenever the feature group becomes active and false when it becomes inactive
     */
    isActiveFeatureGroupId$(featureGroupId: string): Observable<boolean> {

        return this._routeChange$.pipe(
            map((routeChange) => !!routeChange?.activeFeatureGroupIds?.has(featureGroupId)),
            distinctUntilChanged()
        );
    }

    /**
     * Returns true if the feature group with the id is active
     */
    isActiveFeatureGroupId(featureGroupId: string): boolean {

        return !!this._routeChange$.value?.activeFeatureGroupIds?.has(featureGroupId);
    }

    /**
     * Emits true if the route with the featureGroupId and routeId is active
     * When they become inactive it will emit false
     */
    isActiveRouteId$(featureGroupId: string, routeId: string): Observable<boolean> {

        return this._routeChange$.pipe(
            map((routeChange) => routeChange?.featureGroupId === featureGroupId && routeChange?.routeId === routeId),
            distinctUntilChanged()
        );
    }

    /**
     * Returns true if the route with the featureGroupId and routeId is active
     */
    isActiveRouteId(featureGroupId: string, routeId: string): boolean {

        const routeChange = this._routeChange$.value;
        return routeChange?.featureGroupId === featureGroupId && routeChange?.routeId === routeId;
    }

    /**
     * Returns the Angular Router Navigation Commands for a given feature group and route.
     *
     * Performs a fresh AppUtil lookup each time it is called, so it is safe to use from
     * lazy-loaded feature groups where cached nav command data may be stale.
     *
     * @param featureGroupId The feature group that owns the route
     * @param routeId The route identifier within the feature group
     * @param pathParams Optional path parameters to substitute into the route path
     * @returns Array of route command segments for Angular Router navigation
     * @throws Error if the feature group or route cannot be found, or if required path params are missing
     *
     * @example
     * ```typescript
     * const commands = this.mgdRouter.getCommandsForRoute('campaign-feature', 'campaign-list');
     * this.mgdRouter.navigate(commands, NavigationSource.RouterService);
     * ```
     */
    getCommandsForRoute(
        featureGroupId: string, routeId: string, pathParams?: Record<string, string>
    ): ReadonlyArray<string> {

        const {
            navCommand, parentCommands
        } = this._getNavCommandData(featureGroupId, routeId);
        return navCommand.getCommands(pathParams, parentCommands);
    }

    /**
     * Returns the route path string for a given feature group and route.
     *
     * Performs a fresh AppUtil lookup each time it is called, so it is safe to use from
     * lazy-loaded feature groups where cached nav command data may be stale.
     *
     * @param featureGroupId The feature group that owns the route
     * @param routeId The route identifier within the feature group
     * @param pathParams Optional path parameters to substitute into the route path
     * @returns The complete route path as a string (e.g. "/designer/campaign/abc123")
     * @throws Error if the feature group or route cannot be found, or if required path params are missing
     *
     * @example
     * ```typescript
     * const path = this.mgdRouter.getPathForRoute('campaign-feature', 'campaign-list');
     * // path => "/designer/campaign/list"
     * ```
     */
    getPathForRoute(featureGroupId: string, routeId: string, pathParams?: Record<string, string>): string {

        return this.getCommandsForRoute(featureGroupId, routeId, pathParams).join(NG_ROUTE_PATH_DIVIDER);
    }

    /**
     * Returns an Angular UrlTree for a given feature group and route.
     *
     * Performs a fresh AppUtil lookup each time it is called, so it is safe to use from
     * lazy-loaded feature groups where cached nav command data may be stale.
     *
     * @param featureGroupId The feature group that owns the route
     * @param routeId The route identifier within the feature group
     * @param pathParams Optional path parameters to substitute into the route path
     * @returns UrlTree instance for the specified route
     * @throws Error if the feature group or route cannot be found, or if required path params are missing
     *
     * @example
     * ```typescript
     * const urlTree = this.mgdRouter.getUrlTreeForRoute('campaign-feature', 'campaign-list');
     * ```
     */
    getUrlTreeForRoute(featureGroupId: string, routeId: string, pathParams?: Record<string, string>): UrlTree {

        const commands = this.getCommandsForRoute(featureGroupId, routeId, pathParams);
        return this.getUrlTree(commands);
    }

    /**
     * Returns a RedirectCommand for a given feature group and route.
     *
     * Performs a fresh AppUtil lookup each time it is called, so it is safe to use from
     * lazy-loaded feature groups where cached nav command data may be stale.
     *
     * @param featureGroupId The feature group that owns the route
     * @param routeId The route identifier within the feature group
     * @param pathParams Optional path parameters to substitute into the route path
     * @param source Optional navigation source, defaults to NavigationSource.RouterService
     * @returns RedirectCommand for the specified route
     * @throws Error if the feature group or route cannot be found, or if required path params are missing
     *
     * @example
     * ```typescript
     * // In a guard
     * return this.mgdRouter.getRedirectCommandForRoute('campaign-feature', 'campaign-list');
     * ```
     */
    getRedirectCommandForRoute(
        featureGroupId: string,
        routeId: string,
        pathParams?: Record<string, string>,
        source?: NavigationSource
    ): RedirectCommand {

        const tree = this.getUrlTreeForRoute(featureGroupId, routeId, pathParams);
        return this.getRedirectCommand(tree, source ?? NavigationSource.RouterService);
    }

    /**
     * Navigates to a route identified by feature group and route ID.
     *
     * Performs a fresh AppUtil lookup each time it is called, so it is safe to use from
     * lazy-loaded feature groups where cached nav command data may be stale.
     *
     * This method correctly passes queryParams as navigation options, fixing a bug in
     * AbstractRouterService._navigateByRouteId where queryParams were passed directly as
     * INavigationOptions and silently dropped.
     *
     * @param featureGroupId The feature group that owns the route
     * @param routeId The route identifier within the feature group
     * @param pathParams Optional path parameters to substitute into the route path
     * @param queryParams Optional query parameters to include in the navigation
     * @param navigationSource Optional navigation source, defaults to NavigationSource.RouterService
     * @throws Error if the feature group or route cannot be found, or if required path params are missing
     *
     * @example
     * ```typescript
     * this.mgdRouter.navigateByRouteId(
     *   'campaign-feature',
     *   'campaign-edit',
     *   { campaignId: '123' },
     *   { tab: 'details' }
     * );
     * ```
     */
    navigateByRouteId(
        featureGroupId: string,
        routeId: string,
        pathParams?: Record<string, string>,
        queryParams?: Record<string, string>,
        navigationSource?: NavigationSource
    ): void {

        const commands = this.getCommandsForRoute(featureGroupId, routeId, pathParams);
        this.navigate(commands as Array<string>, navigationSource ?? NavigationSource.RouterService, { queryParams });
    }

    /**
     * Returns the sorted sidenav links for a given feature group.
     *
     * Performs a fresh AppUtil lookup each time it is called, so it is safe to use from
     * lazy-loaded feature groups where cached nav command data may be stale.
     *
     * @param featureGroupId The feature group to get sidenav links for
     * @returns Sorted array of ISidenavLink objects, or empty array if no nav command data exists
     *
     * @example
     * ```typescript
     * const links = this.mgdRouter.getSidenavLinks('campaign-feature');
     * ```
     */
    getSidenavLinks(featureGroupId: string): ReadonlyArray<ISidenavLink> {

        const navCommandData: FeatureGroupNavCommandData = AppUtil.getNavCommandData(featureGroupId);
        if (!navCommandData) {
            return [];
        }
        return Array.from(navCommandData.values())
            .filter((route) => !!route.sidenavLink)
            .map((route) => route.sidenavLink)
            .sort((a, b) => a.sortOrder - b.sortOrder);
    }

    /**
     * Preloads route resolvers and returns their resolved values or a redirect command.
     *
     * This function should be called in Guards, since guards execute before data resolvers.
     * It allows you to run resolvers early in the navigation lifecycle and handle any
     * redirects that might be needed based on the resolved data.
     *
     * @param route The activated route snapshot containing route information
     * @param state The router state snapshot containing navigation state
     * @param resolvers Array of resolver functions to execute in sequence
     * @returns Observable that emits either an array of resolved values or a RedirectCommand
     * if any resolver returns a redirect
     *
     * @example
     * ```typescript
     * // In a Guard
     * canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | RedirectCommand> {
     *   return this.mgdRouter.preloadResolvers$(route, state, [userResolver, permissionsResolver])
     *     .pipe(
     *       map(result => {
     *         if (result instanceof RedirectCommand) {
     *           return result;
     *         }
     *         const [user, permissions] = result;
     *         return this.checkPermissions(user, permissions);
     *       })
     *     );
     * }
     * ```
     */
    preloadResolvers$(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
        resolvers: Array<ResolveFn<unknown>>
    ): Observable<Array<unknown> | RedirectCommand> {

        return this._processResolvers$(resolvers, 0, route, state, []);
    }

    /**
     * Recursively builds the parent command chain for a feature group.
     *
     * @param featureGroupId The feature group whose parent chain should be built
     * @returns Array of parent RouteNavCommandData in order from root to immediate parent
     * @throws Error if parent nav command data cannot be found for any non-app feature group
     */
    private _getParentCommands(featureGroupId: string): ReadonlyArray<RouteNavCommandData> {

        if (featureGroupId === APP_FEATURE_GROUP_ID) {
            return [];
        }
        const parentNavCommand = AppUtil.getParentNavCommandData(featureGroupId);
        if (!parentNavCommand) {
            throw new Error(`No parent nav command data registered for ${featureGroupId}`);
        }
        return [...this._getParentCommands(parentNavCommand.featureGroupId), parentNavCommand];
    }

    /**
     * Gets nav command data for a specific feature group and route.
     *
     * @param featureGroupId The feature group that owns the route
     * @param routeId The route identifier within the feature group
     * @returns Object with navCommand and parentCommands for the specified route
     * @throws Error if nav command data is not found for the feature group, or if the routeId is not found
     */
    private _getNavCommandData(
        featureGroupId: string, routeId: string
    ): {
            navCommand: RouteNavCommandData;
            parentCommands: ReadonlyArray<RouteNavCommandData>;
        } {

        const navCommandData: FeatureGroupNavCommandData = AppUtil.getNavCommandData(featureGroupId);
        if (!navCommandData) {
            throw new Error(`No nav command data registered for feature group ${featureGroupId}`);
        }
        const navCommand = navCommandData.get(routeId);
        if (!navCommand) {
            throw new Error(`No route found for routeId ${routeId} in feature group ${featureGroupId}`);
        }
        const parentCommands = this._getParentCommands(featureGroupId);
        return {
            navCommand,
            parentCommands
        };
    }

    /**
     * Recursively processes resolvers in sequence, handling both sync and async resolvers.
     *
     * Short-circuits if a resolver returns a RedirectCommand.
     *
     * @param resolvers Array of resolver functions to process
     * @param currentIndex Current index in the resolvers array being processed
     * @param route The activated route snapshot
     * @param state The router state snapshot
     * @param results Accumulated results from previously processed resolvers
     * @returns Observable that emits either accumulated results or a RedirectCommand
     */
    private _processResolvers$(
        resolvers: Array<ResolveFn<unknown>>,
        currentIndex: number,
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
        results: Array<unknown>
    ): Observable<Array<unknown> | RedirectCommand> {

        if (currentIndex >= resolvers.length) {
            return of(results);
        }

        const resolver = resolvers[currentIndex];
        const result = runInInjectionContext(this._injector, () => resolver(route, state));

        return RxjsUtil.handleMaybeAsync$(result).pipe(
            switchMap((resolverResult) => {
                if (resolverResult instanceof RedirectCommand) {
                    return of(resolverResult);
                }
                return this._processResolvers$(resolvers, currentIndex + 1, route, state, [...results, resolverResult]);
            })
        );
    }

    /**
     * returns the Angular Router Navigation Extras based on common options
     */
    private _getNavigationExtras(source?: NavigationSource, options?: INavigationOptions): NavigationExtras {

        return {
            queryParams: options?.queryParams,
            info: source,
            state: {
                errorMessage: options?.errorMessage
            },
            relativeTo: options?.relativeToCurrentRoute ? this.route : this.route.root
        };
    }

    /**
     * Setup the route change subscription
     *
     * This will listen on the router events and will emit the routeChange$
     */
    private _setupRouteChangeSubscription(): void {
        // Update the route state when navigation is completed
        this._router.events.pipe(
            // Navigation is completed
            filter((event) => {
                return event instanceof NavigationEnd || event instanceof NavigationError;
            }),
            map(() => this._createActiveRoute()),
            // This subscription will complete when this service is destroyed
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(this._routeChange$);
    }

    /**
     * Creates an IActiveRoute object from the angular Router
     *
     * If the router is not initialized or the current route is a legacy route it returns undefined
     */
    private _createActiveRoute(): IActiveRoute {
        let route = this.route;
        let data: Partial<IRouteData> = route?.snapshot?.data;
        let featureGroupId = data?.featureGroupId;
        let routeId = data?.routeId;
        if (!featureGroupId || !routeId) {
            return undefined;
        }
        const active: Partial<IActiveRoute> = {
            ...data,
            activeFeatureGroupIds: new Set<string>([featureGroupId]),
            activeFeatureGroupLoadingRoutes: new Array<IActiveRoute>(),
            route
        };
        let lastFeatureGroupLoadingRoute: Partial<IRouteData> = active;
        while (route.parent) {
            // Loop until we get to the first route in the hierarchy
            route = route.parent;
            data = route?.snapshot?.data;
            featureGroupId = data?.featureGroupId;
            if (featureGroupId) {
                active.exitPath = active.exitPath ?? data.exitPath;
                if (
                    featureGroupId !== lastFeatureGroupLoadingRoute.featureGroupId
                    && lastFeatureGroupLoadingRoute.featureGroupId !== featureGroupId) {
                    lastFeatureGroupLoadingRoute = data;
                    active.activeFeatureGroupIds.add(featureGroupId);
                    active.activeFeatureGroupLoadingRoutes.push(data as IRouteData);
                }
            }
        }

        active.exitPath = active.exitPath ?? this.rootPath;
        return active as IActiveRoute;
    }

}
